import React, { useState, useCallback, useMemo } from 'react';
import { parseISO, compareAsc } from 'date-fns';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from 'javascripts/hooks';
import UploadService from 'services/upload-service';
import { createTag } from 'actions';
import { createImageForProject } from 'helpers/photos';
import BeforeAfterPhotoModal from 'components/before_and_after/BeforeAfterPhotoModal';
import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';
import { useModal } from 'components/shared/hooks';
import { Modal } from 'components/core/modal/constants';
import { canAccessFeature, FEATURES } from 'helpers/features';

const databaseIdFrom = (obj) => parseInt(obj.databaseId, 10);

export default function useMenuActionBeforeAfter({ assets, assetsByType }) {
  const [modalVisible, setModalVisible] = useState(false);
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { showModal, hideModal } = useModal();
  const { featureSet } = useCompanyBillingStatus();

  const canAccessBeforeAfterPhotos = canAccessFeature(
    featureSet,
    FEATURES.BEFORE_AFTER_PHOTOS,
  );

  const projectInfo = assets?.[0]?.projectInfo;
  const projectDatabaseId = projectInfo ? databaseIdFrom(projectInfo) : 0;

  // If one photo is selected, see if it has B&A composite metadata with IDs
  const idsExisting = useMemo(() => {
    const onePhoto = assets.length === 1 && assetsByType.photos.length === 1;
    if (!onePhoto) {
      return null;
    }
    const { before_id, after_id } = assets[0]?.metadata ?? {};
    return before_id && after_id ? [before_id, after_id] : null;
  }, [assets, assetsByType]);

  // If two photos are selected, try sorting by date and then get IDs
  const idsNew = useMemo(() => {
    const twoPhotos = assets.length === 2 && assetsByType.photos.length === 2;
    if (!twoPhotos) {
      return null;
    }

    const sortedAssets = [assets[0], assets[1]];
    if (sortedAssets[0].capturedAt && sortedAssets[1].capturedAt) {
      sortedAssets.sort((a, b) =>
        compareAsc(parseISO(a.capturedAt), parseISO(b.capturedAt)),
      );
    }
    return [databaseIdFrom(sortedAssets[0]), databaseIdFrom(sortedAssets[1])];
  }, [assets, assetsByType]);

  const photoDatabaseIds = idsExisting || idsNew || null;
  const validSelection = !!photoDatabaseIds;

  const action = useCallback(() => {
    if (canAccessBeforeAfterPhotos) {
      setModalVisible(true);
    } else {
      showModal(Modal.UPGRADE_BEFORE_AFTER, {
        onRequestClose: () => hideModal(),
      });
    }
  }, [canAccessBeforeAfterPhotos, showModal, hideModal]);

  const onCloseModal = useCallback(() => {
    setModalVisible(false);
  }, [setModalVisible]);

  const renderedModal = modalVisible && validSelection && (
    <BeforeAfterPhotoModal
      imageBeforeId={photoDatabaseIds[0]}
      imageAfterId={photoDatabaseIds[1]}
      companyId={user.company_id}
      onCancel={onCloseModal}
      onSave={async (dataURL) => {
        onCloseModal();

        const uploadService = new UploadService();
        const imageURI = await uploadService.uploadDataURLToS3(dataURL);

        const beforeAndAfterTag = { display_value: 'Before and After' };
        const tag = await dispatch(createTag(beforeAndAfterTag));

        const params = {
          metadata: {
            before_id: photoDatabaseIds[0],
            after_id: photoDatabaseIds[1],
          },
          tags: [tag.id],
        };
        await createImageForProject(
          user.id,
          imageURI,
          projectDatabaseId,
          params,
        );

        // Log event
        CCEvents.logEvent(EventNames.CREATE_BEFORE_AFTER);
      }}
    />
  );

  return {
    action,
    validSelection,
    canAccessBeforeAfterPhotos,
    renderedModal,
  };
}
